import { useCallback } from "react";
import { each, map, some } from "lodash";
import {
  QueryClient,
  QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { PostAPI } from "@radarly/api/post";
import { Post, UpdatedPostData, getUpdatedPost } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";
import { SearchFragmentPosts } from "@radarly/models/search";

const api = new PostAPI();

function hasSomePost(posts: Post[], post: Post): boolean {
  return some(posts, { uid: post.uid });
}

function setPostQueryData(
  queryClient: QueryClient,
  key: QueryKey,
  updatedHits: Post[]
) {
  queryClient.setQueryData<SearchFragmentPosts["data"]>(key, (queryData) => {
    if (queryData.hits) {
      return {
        ...queryData,
        hits: updatedHits,
      };
    }
    return queryData;
  });
}

function getUpdatedHitsFromMutatedPost(
  hits: Post[],
  post: Post,
  doc: UpdatedPostData
) {
  return map(hits, (hit) => {
    return hit.uid === post.uid ? getUpdatedPost(hit, doc) : hit;
  });
}

interface InfinitePostQueryData {
  pages: SearchFragmentPosts["data"][];
}

type PostQueryData = SearchFragmentPosts["data"] | InfinitePostQueryData;

interface MutationData {
  post: Post;
  doc: UpdatedPostData;
}

interface MutationContext {
  previousQueries: [QueryKey, PostQueryData][];
}

function isInfinitePostQueryData(
  queryData: PostQueryData
): queryData is InfinitePostQueryData {
  return !!(queryData as InfinitePostQueryData)?.pages;
}

function onMutatePost(
  queryClient: QueryClient,
  mutationData: MutationData
): MutationContext {
  const previousQueries = queryClient.getQueriesData<PostQueryData>([
    "/search/v3",
  ]);
  each(previousQueries, (previousQuery) => {
    const [previousQueryKey, previousQueryData] = previousQuery;
    if (previousQueryData && previousQueryKey) {
      if (isInfinitePostQueryData(previousQueryData)) {
        const pages = map(previousQueryData.pages, (previousQueryDataPage) => {
          if (hasSomePost(previousQueryDataPage.hits, mutationData.post)) {
            return {
              ...previousQueryDataPage,
              hits: getUpdatedHitsFromMutatedPost(
                previousQueryDataPage.hits,
                mutationData.post,
                mutationData.doc
              ),
            };
          } else {
            return previousQueryDataPage;
          }
        });
        queryClient.setQueryData<InfinitePostQueryData>(
          previousQueryKey,
          (previousData) => {
            return {
              ...previousData,
              pages,
            };
          }
        );
      } else {
        if (hasSomePost(previousQueryData.hits, mutationData.post)) {
          setPostQueryData(
            queryClient,
            previousQueryKey,
            getUpdatedHitsFromMutatedPost(
              previousQueryData.hits,
              mutationData.post,
              mutationData.doc
            )
          );
        }
      }
    }
  });
  return {
    previousQueries,
  };
}

function handleErrorMutationOnInfiniteQueryData(
  queryClient: QueryClient,
  queryKey: QueryKey,
  queryData: InfinitePostQueryData,
  post: Post
) {
  const hasSomePostOnPages = some(queryData.pages, (previousQueryDataPage) => {
    return hasSomePost(previousQueryDataPage.hits, post);
  });
  if (hasSomePostOnPages) {
    queryClient.setQueryData<InfinitePostQueryData>(queryKey, queryData);
  }
}

function handleErrorMutationOnQuery(
  queryClient: QueryClient,
  query: [QueryKey, PostQueryData],
  post: Post
) {
  const [previousQueryKey, previousQueryData] = query;
  if (previousQueryData && previousQueryKey) {
    if (isInfinitePostQueryData(previousQueryData)) {
      handleErrorMutationOnInfiniteQueryData(
        queryClient,
        previousQueryKey,
        previousQueryData,
        post
      );
    } else {
      if (hasSomePost(previousQueryData.hits, post)) {
        setPostQueryData(queryClient, previousQueryKey, previousQueryData.hits);
      }
    }
  }
}

function onMutationError(
  queryClient: QueryClient,
  mutationData: MutationData,
  context: MutationContext
) {
  each(context.previousQueries, (previousQuery) => {
    handleErrorMutationOnQuery(queryClient, previousQuery, mutationData.post);
  });
}

/*
function isQueryKeyisRelatedToSearch(queryKey: QueryKey) {
  return (
    isArray(queryKey) && queryKey.length > 0 && queryKey[0] === "/search/v3"
  );
}

function isQueryWithPost(query: Query, post: Post) {
  const data = query?.state?.data as SearchFragmentPosts["data"];
  if (isQueryKeyisRelatedToSearch(query.queryKey) && !!data && !!data.hits) {
    return hasSomePost(data.hits, post);
  }
  return false;
}
*/

function onMutationSettled(
  queryClient: QueryClient,
  mutationData: MutationData
) {
  // TODO : Handle background query fetching. Using isLoading instead of isFetching removes the loading spinner
  /*
  queryClient.invalidateQueries({
    predicate: (query) => {
      return isQueryWithPost(query, mutationData.post);
    },
  });
  */
}

export function useUpdatePost(
  project: Project
): (post: Post, updatedData: object) => void {
  const queryClient = useQueryClient();
  const onMutate = useCallback(
    async (mutationData: MutationData) => {
      return onMutatePost(queryClient, mutationData);
    },
    [queryClient]
  );
  const onError = useCallback(
    async (err, mutationData: MutationData, context: MutationContext) => {
      return onMutationError(queryClient, mutationData, context);
    },
    [queryClient]
  );
  const onSettled = useCallback(
    async (post: Post, error, mutationData: MutationData) => {
      return onMutationSettled(queryClient, mutationData);
    },
    [queryClient]
  );
  const { mutateAsync } = useMutation(
    ({ post, doc }: MutationData) => {
      return api.updatePost(project.id, post, doc);
    },
    {
      onMutate,
      onError,
      onSettled,
    }
  );
  return (post: Post, doc: UpdatedPostData) => {
    return mutateAsync({
      post,
      doc,
    });
  };
}
